import React, { useEffect, useRef, useState } from 'react'
import { MenuItem } from '..'
import type { CategoryTree, Maybe } from '@pylot-data/pylotschema'
import { getMenuItemSlug } from '../../utils/getMenuItemStableClassNames'
import s from '@commonStyles/menus/DesktopMegamenu.module.scss'

type DesktopMegaMenuProps = {
    navData: CategoryTree
}

export const sortCategories = (
    a: Maybe<CategoryTree>,
    b: Maybe<CategoryTree>
): number => {
    if (!a?.position || !b?.position) {
        return 0
    }
    return a.position - b.position
}

export const DesktopMegaMenu = ({
    navData
}: DesktopMegaMenuProps): JSX.Element | null => {
    const items = navData?.children

    const [openKey, setOpenKey] = useState<string | null>(null)
    const listRef = useRef<HTMLUListElement>(null)

    useEffect(() => {
        if (!openKey) return

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target
            if (!(target instanceof Node)) return
            const current = listRef.current?.querySelector(
                `[data-menu-url-key="${openKey}"]`
            )
            if (current?.contains(target)) return
            setOpenKey(null)
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpenKey(null)
        }

        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [openKey])

    if (!items) return null
    const sortedItems = [...items].sort(sortCategories)

    return (
        <div className={s['desktop-megamenu']}>
            <div className={s['desktop-megamenu-inner']}>
                <ul className={s['desktop-megamenu-list']} ref={listRef}>
                    {sortedItems.map((item) => {
                        const key = item ? getMenuItemSlug(item) : undefined
                        return (
                            <MenuItem
                                key={item?.url_key}
                                item={item}
                                siblingItems={sortedItems}
                                isMenuOpen={openKey === key}
                                onMenuOpenChange={(next) =>
                                    setOpenKey(next ? key ?? null : null)
                                }
                            />
                        )
                    })}
                </ul>
            </div>
        </div>
    )
}

export default DesktopMegaMenu
